import { type Registry as Services, service } from '@ember/service';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import CentralMappingMappingEntrySerializer from 'lumen/serializers/central-mapping/mapping-entry';
import {
  DAY_OF_WEEK_BREAKDOWN_VALUE_TRANSLATIONS,
  isDayOfWeekBreakdownValue,
} from 'lumen/utils/central-mapping-constants';

export default class CentralMappingMappingEntryDayOfWeekSerializer extends CentralMappingMappingEntrySerializer {
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  modelNameFromPayloadKey(key: string) {
    if (key === 'data') {
      return super.modelNameFromPayloadKey(
        'central-mapping/mapping-entry-day-of-week',
      );
    }

    return key;
  }

  /**
   * Adjust the payload so that the mapping entry value is represented as a translated weekday string.
   * This is required because the backend provides the value as a number, but the UI displays the translated string.
   * The raw value is preserved to convert the value back to a number when sending the payload to the backend.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  normalizeResponse(
    store: Store,
    primaryModelClass: ModelSchema,
    payload: { data: Record<string, unknown>[]; meta: Record<string, object> },
    id: string | number,
    requestType: string,
  ) {
    payload.data.forEach((weekdayMappingEntry) => {
      weekdayMappingEntry['raw_value'] = weekdayMappingEntry['value'];

      const dayOfWeekValueNumber = weekdayMappingEntry['value'] as number;

      if (!isDayOfWeekBreakdownValue(dayOfWeekValueNumber)) {
        throw new Error(
          `Invalid day of week value: ${dayOfWeekValueNumber}. Must be between 1 and 7.`,
        );
      }

      const dayOfWeekTranslationString =
        DAY_OF_WEEK_BREAKDOWN_VALUE_TRANSLATIONS[dayOfWeekValueNumber];

      weekdayMappingEntry['value'] = this.intl.t(dayOfWeekTranslationString);

      return weekdayMappingEntry;
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return super.normalizeResponse(
      store,
      primaryModelClass,
      payload,
      id,
      requestType,
    );
  }

  /**
   * Converts the mapping entry value back to its original numeric form during serialization.
   * This ensures the value, which was transformed into a weekday string for UI display, is restored to its raw format for backend communication.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  serialize(snapshot: Snapshot, options: object) {
    const payload = super.serialize(snapshot, options);

    if ('raw_value' in payload && 'value' in payload) {
      payload.value = payload.raw_value;
      delete payload.raw_value;
    }

    return payload;
  }
}
